import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import BinarySearchAnimation from "./BinarySearchAnimation";
import MatrixSearchAnimation from "./2DMatrixAnimation";
import BFSAnimation from "./BFSAnimation";
import DFSAnimation from "./DFSAnimation";
import SlidingWindowAnimation from "./SlidingWindowAnimation";
import HashAnimation from "./HashAnimation";
import UserSideBar from "./userSideBar";

const LeetCodePage = () => {
    const navigate = useNavigate();
    const [username, setUsername] = useState("");
        
        useEffect(() => {
            const params = new URLSearchParams(window.location.search);
            const accessToken = params.get("token");
            const usernameParam = params.get("username");
        
            if (accessToken) {
                localStorage.setItem("access_token", accessToken);
                if (usernameParam) {
                    localStorage.setItem("username", usernameParam);
                    setUsername(usernameParam);
                }
                // Remove token from URL
                window.history.replaceState({}, document.title, "/user");
            } 
                // Else if we don't have a token stored, redirect to login
            else if (!localStorage.getItem("access_token")) {
                alert("You are not logged in. Redirecting to login...");
                navigate("/");
            } 
                // Otherwise, fetch the username from localStorage
            else {
                setUsername(localStorage.getItem("username") || "User");
            }
        }, [navigate]);
  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 py-8 px-4">
      {/* Page Title */}
      <h1 className="text-4xl font-bold text-center text-gray-900 dark:text-white mb-8">
        XU's Leetcode Note
      </h1>
      <UserSideBar />
      <div className="max-w-4xl mx-auto space-y-8">
        
        {/* Binary Search Section */}
        <section className="bg-white dark:bg-gray-800 rounded-md shadow p-6">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-200 mb-4">
            Binary Search
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Binary Search is an efficient algorithm for finding an item from a 
            sorted list of items. It works by repeatedly dividing the search 
            interval in half. If the value of the search key is less than the 
            item in the middle of the interval, narrow the interval to the 
            lower half. Otherwise narrow it to the upper half. Repeatedly check 
            until the value is found or the interval is empty.
          </p>
          <pre className="p-4 bg-gray-100 dark:bg-gray-700 text-sm rounded-md overflow-auto text-gray-800 dark:text-gray-200">
{`# Python code for Binary Search
def binary_search(nums, target):
    left, right = 0, len(nums) - 1
    
    while left <= right:
        mid = (left + right) // 2
        
        if nums[mid] == target:
            return mid  # Found the target
        elif nums[mid] < target:
            left = mid + 1
        else:
            right = mid - 1
    
    return -1  # Target not found
`}
          </pre>
          <p className="text-gray-700 dark:text-gray-300 mt-4">
            <strong>Time Complexity:</strong> O(log n)
          </p>
          <BinarySearchAnimation />
        </section>

        {/* 2D Matrix Search Section */}
        <section className="bg-white dark:bg-gray-800 rounded-md shadow p-6">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-200 mb-4">
            Binary Search in a 2D Matrix
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Often, Leetcode problems involve searching a sorted 2D matrix. One 
            approach is to treat the matrix like a single sorted list. Convert 
            a 2D index \`(row, col)\` into a 1D index by:
          </p>
          <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4">
            <li><code>mid_row = mid // number_of_columns</code></li>
            <li><code>mid_col = mid % number_of_columns</code></li>
          </ul>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Then apply a normal binary search on this virtual 1D array, comparing 
            the middle element with the target. If the matrix is sorted in a 
            different manner (e.g., row-wise or column-wise), there can be alternate 
            pointer-based solutions as well.
          </p>
          <pre className="p-4 bg-gray-100 dark:bg-gray-700 text-sm rounded-md overflow-auto text-gray-800 dark:text-gray-200">
{`# Python code for searching a 2D matrix (treating it as 1D)
def search_matrix(matrix, target):
    if not matrix or not matrix[0]:
        return False
    
    rows = len(matrix)
    cols = len(matrix[0])
    left, right = 0, rows * cols - 1
    
    while left <= right:
        mid = (left + right) // 2
        mid_row = mid // cols
        mid_col = mid % cols
        
        if matrix[mid_row][mid_col] == target:
            return True
        elif matrix[mid_row][mid_col] < target:
            left = mid + 1
        else:
            right = mid - 1
    
    return False
`}
          </pre>
          <p className="text-gray-700 dark:text-gray-300 mt-4">
            <strong>Time Complexity:</strong> O(log (m*n)), where m is the number of rows and n is the number of columns.
          </p>
          <MatrixSearchAnimation />
        </section>

        <section className="bg-white dark:bg-gray-800 rounded-md shadow p-6">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-200 mb-4">
            Sliding Window
        </h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
            The Sliding Window technique is used to perform a series of computations
            (like sums, maximums, or minimums) on consecutive elements of an array 
            (or list). By moving the start and end of the window across the data,
            we can solve many problems in linear time.
        </p>
        <pre className="p-4 bg-gray-100 dark:bg-gray-700 text-sm rounded-md overflow-auto text-gray-800 dark:text-gray-200">
{`# Python code for Sliding Window (Example: max sum of a subarray of size k)

def max_subarray_sum(nums, k):
    if not nums or k <= 0 or k > len(nums):
        return 0
    
    window_sum = 0
    max_sum = float('-inf')
    
    # Initialize the first window
    for i in range(k):
        window_sum += nums[i]
    max_sum = window_sum
    
    # Slide the window from index k to the end
    for i in range(k, len(nums)):
        # Subtract the element going out and add the new element
        window_sum += nums[i] - nums[i - k]
        max_sum = max(max_sum, window_sum)
    
    return max_sum
`}
        </pre>
        <p className="text-gray-700 dark:text-gray-300 mt-4">
            <strong>Time Complexity:</strong> O(n)
        </p>
        <SlidingWindowAnimation />
        </section>

        <section className="bg-white dark:bg-gray-800 rounded-md shadow p-6 mt-6">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-200 mb-4">
            Hash (Hash Map / Dictionary)
        </h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
            A Hash (or Hash Map) is a data structure that provides an average-case 
            lookup time of O(1), making it very efficient for many applications. 
            In Python, dictionaries are used as hash maps, allowing you to store 
            and retrieve data via key-value pairs.
        </p>
        <pre className="p-4 bg-gray-100 dark:bg-gray-700 text-sm rounded-md overflow-auto text-gray-800 dark:text-gray-200">
{`# Python code for using a hash map (dictionary) (Example: two-sum problem)

def two_sum(nums, target):
    hash_map = {}
    
    for i, num in enumerate(nums):
        complement = target - num
        if complement in hash_map:
            return [hash_map[complement], i]  # Indices of the two numbers
        hash_map[num] = i
    
    return [-1, -1]  # Indicate not found
`}
        </pre>
        <p className="text-gray-700 dark:text-gray-300 mt-4">
            <strong>Time Complexity:</strong> O(n)
        </p>
        <HashAnimation />
        </section>


        <section className="bg-white dark:bg-gray-800 rounded-md shadow p-6">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-200 mb-4">
                Breadth-First Search (BFS)
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
                Breadth-First Search is an algorithm for traversing or searching a tree or
                graph data structure. It explores the neighbor nodes first, before moving
                on to the next level neighbors.
            </p>
            <pre className="p-4 bg-gray-100 dark:bg-gray-700 text-sm rounded-md overflow-auto text-gray-800 dark:text-gray-200">
            {`# Python code for BFS
from collections import deque

def bfs(graph, start):
    visited = set()
    queue = deque([start])
    visited.add(start)

    while queue:
        vertex = queue.popleft()
        
        # Process or print vertex here if needed
        for neighbor in graph[vertex]:
            if neighbor not in visited:
                visited.add(neighbor)
                queue.append(neighbor)
`}
            </pre>
            <p className="text-gray-700 dark:text-gray-300 mt-4">
                <strong>Time Complexity:</strong> O(V + E) where V is the number of vertices
                and E is the number of edges.
            </p>
            <BFSAnimation />
        </section>

        <section className="bg-white dark:bg-gray-800 rounded-md shadow p-6">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-200 mb-4">
                Depth-First Search (DFS)
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
                Depth-First Search is an algorithm for traversing or searching a tree or
                graph data structure. It explores as far as possible along each branch
                before backtracking.
            </p>
            <pre className="p-4 bg-gray-100 dark:bg-gray-700 text-sm rounded-md overflow-auto text-gray-800 dark:text-gray-200">
            {`# Python code for DFS (recursive version)
def dfs(graph, start, visited=None):
    if visited is None:
        visited = set()
    
    visited.add(start)
    # Process or print vertex here if needed
    
    for neighbor in graph[start]:
        if neighbor not in visited:
            dfs(graph, neighbor, visited)
`}
            </pre>
            <p className="text-gray-700 dark:text-gray-300 mt-4">
                <strong>Time Complexity:</strong> O(V + E) where V is the number of vertices
                and E is the number of edges.
            </p>
            <DFSAnimation />
        </section>

        
        {/* You can add more sections for other algorithms here */}
        
      </div>
    </div>
  );
};

export default LeetCodePage;
